import { IServerResponse, IConnectionStatus } from '@/app-modules/server-communications';
import { Observable } from 'rxjs/Observable';
import { container } from '@/app-modules/_ioc';
import { SERVICE_IDENTIFIERS } from '@/app-modules/_ioc/service-identifiers';
import { CallsData, CatchUpData, NextGameData, ResetData, PrizeData, BoxNumbersData } from '@/components/game/_domain/server/prize/prize-data';
import {  JsonCallData, JsonCountdownData, JsonWinnersData, JsonWelcomeData, JsonStartData } from '@/components/game/_domain/server/json/prize/json-prize-data';
import { AuthenticationResponse } from '@/components/game/_domain/server/auth/auth-response';
import { WinnersResponse } from '@/components/game/_domain/server/winners/winners-response';
import { CallsResponse } from '@/components/game/_domain/server/calls/calls-response';
import { CatchUpResponse } from '@/components/game/_domain/server/catch-up/catchup-response';
import { ResetResponse } from '@/components/game/_domain/server/reset/reset-response';
import { NextGameResponse } from '@/components/game/_domain/server/next-game/next-game-response';
import { PrizeWinnerData } from '@/components/game/_domain/server/prize-winner/prize-winner-data';
import { JsonCallsResponse } from '@/components/game/_domain/server/json/calls/json-call-response';
import { JsonStartResponse } from '../json/start/json-start-response';
import { JsonWelcomeResponse } from '../json/welcome/json-welcome-response';
import { JsonCountdownResponse } from '../json/countdowns/json-countdown-response';
import { JsonWinnersResponse } from '../json/winners/json-winners-response';
import { WinnersData } from '@/components/game/_domain';
import { PrizeWinnerResponse } from '@/components/game/_domain/server/prize-winner/prize-winner-response';
import { ConnectionStatusConnectingResponse, ConnectionStatusConnectedResponse,
  ConnectionStatusConnectionLostResponse, ConnectionStatusDisconnectingResponse,
  ConnectionStatusDisconnectedResponse, ConnectionStatusRetryCircuitBrokenResponse } from '@/app-modules/connection-status';
import { IServerResponseStreamsResolver, IGameServerServiceStreamProperties, IJsonGameServerServiceStreamProperties, IAuthenticationResponse } from '@/app-modules/server-service';
import { HamsterRaceInfoData } from '@/components/game/_domain/server/hamster-race-info/hamster-race-info-data';
import { GameIdentity } from '@/components/game/_domain/models';
import { HamsterRaceInfoResponse } from '@/components/game/_domain/server/hamster-race-info/hamster-race-info-response';
import { WinnerCatchupData, WinnerCatchupResponse } from '@/components/game/_domain/server/winner-catchup';
import { SetStageData, SetStageResponse } from '@/components/game/_domain/server/set-stage';
import { BoxNumbersResponse } from '../boxnumbers/boxnumbers-response';

class JsonGameServerResponseStreams implements IJsonGameServerServiceStreamProperties{
  public readonly calls$?: Observable<JsonCallData>;
  public readonly winners$?: Observable<JsonWinnersData>;
  public readonly welcome$?: Observable<JsonWelcomeData>;
  public readonly start$?: Observable<JsonStartData>;
  public readonly countdown$?: Observable<JsonCountdownData>;
  public readonly connectionStatusConnecting$: Observable<{}>;
  public readonly connectionStatusConnected$: Observable<{}>;
  public readonly connectionStatusConnectionLost$: Observable<{}>;
  public readonly connectionStatusDisconnecting$: Observable<{}>;
  public readonly connectionStatusDisconnected$: Observable<{}>;
  public readonly connectionStatusRetryCircuitBroken$: Observable<{}>;
  public readonly auth$: any;
  public readonly error$: Observable<Error>;

  constructor(stream$: Observable<IServerResponse>, error$: Observable<Error>, connectionStatus$: Observable<IConnectionStatus>, gameName: GameIdentity){
    this.connectionStatusConnecting$ = ConnectionStatusConnectingResponse.asStream(stream$, error$, connectionStatus$);
    this.connectionStatusConnected$ = ConnectionStatusConnectedResponse.asStream(stream$, error$, connectionStatus$);
    this.connectionStatusConnectionLost$ = ConnectionStatusConnectionLostResponse.asStream(stream$, error$, connectionStatus$);
    this.connectionStatusDisconnecting$ = ConnectionStatusDisconnectingResponse.asStream(stream$, error$, connectionStatus$);
    this.connectionStatusDisconnected$ = ConnectionStatusDisconnectedResponse.asStream(stream$, error$, connectionStatus$);
    this.connectionStatusRetryCircuitBroken$ = ConnectionStatusRetryCircuitBrokenResponse.asStream(stream$, error$, connectionStatus$);
    this.error$ = error$;
    this.calls$ = new JsonCallsResponse(gameName).asStream(stream$, error$, connectionStatus$);
    this.start$ = new JsonStartResponse(gameName).asStream(stream$, error$, connectionStatus$);
    this.welcome$ = new JsonWelcomeResponse(gameName).asStream(stream$, error$, connectionStatus$);
    this.countdown$ = new JsonCountdownResponse(gameName).asStream(stream$, error$, connectionStatus$);
    this.winners$ = new JsonWinnersResponse(gameName).asStream(stream$, error$, connectionStatus$);

  }

  public static createStreamsForGame(gameIdentity: GameIdentity): IServerResponseStreamsResolver {
    return (stream$, error$, connectionStatus$) => new JsonGameServerResponseStreams(stream$, error$, connectionStatus$, gameIdentity);
  }
}

class GameServerResponseStreams implements IGameServerServiceStreamProperties {
  public readonly connectionStatusConnecting$: Observable<{}>;
  public readonly connectionStatusConnected$: Observable<{}>;
  public readonly connectionStatusConnectionLost$: Observable<{}>;
  public readonly connectionStatusDisconnecting$: Observable<{}>;
  public readonly connectionStatusDisconnected$: Observable<{}>;
  public readonly connectionStatusRetryCircuitBroken$: Observable<{}>;
  public readonly error$: Observable<Error>;
  public readonly auth$: Observable<IAuthenticationResponse>;
  public readonly calls$: Observable<CallsData>;
  public readonly winners$: Observable<WinnersData>;
  public readonly catchUp$: Observable<CatchUpData>;
  public readonly reset$: Observable<ResetData>;
  public readonly nextGame$: Observable<NextGameData>;
  public readonly prizeWinner$: Observable<PrizeWinnerData>;
  public readonly hamsterRaceInfo$: Observable<HamsterRaceInfoData>;
  public readonly winnerCatchUp$: Observable<WinnerCatchupData>;
  public readonly setStage$: Observable<SetStageData>;
  public readonly boxNumbers$: Observable<BoxNumbersData>;

  constructor(stream$: Observable<IServerResponse>, error$: Observable<Error>, connectionStatus$: Observable<IConnectionStatus>, gameName: GameIdentity) {
    this.connectionStatusConnecting$ = ConnectionStatusConnectingResponse.asStream(stream$, error$, connectionStatus$);
    this.connectionStatusConnected$ = ConnectionStatusConnectedResponse.asStream(stream$, error$, connectionStatus$);
    this.connectionStatusConnectionLost$ = ConnectionStatusConnectionLostResponse.asStream(stream$, error$, connectionStatus$);
    this.connectionStatusDisconnecting$ = ConnectionStatusDisconnectingResponse.asStream(stream$, error$, connectionStatus$);
    this.connectionStatusDisconnected$ = ConnectionStatusDisconnectedResponse.asStream(stream$, error$, connectionStatus$);
    this.connectionStatusRetryCircuitBroken$ = ConnectionStatusRetryCircuitBrokenResponse.asStream(stream$, error$, connectionStatus$);
    this.error$ = error$;
    this.auth$ = AuthenticationResponse.asStream(stream$, error$, connectionStatus$);
    this.calls$ = new CallsResponse(container.get(SERVICE_IDENTIFIERS.ICallsDataFactory), gameName).asStream(stream$, error$, connectionStatus$);
    this.winners$ = new WinnersResponse(container.get(SERVICE_IDENTIFIERS.IWinnersDataFactory), gameName).asStream(stream$, error$, connectionStatus$);
    this.catchUp$ = new CatchUpResponse(container.get(SERVICE_IDENTIFIERS.ICatchUpDataFactory), gameName).asStream(stream$, error$, connectionStatus$);
    this.reset$ = new ResetResponse(container.get(SERVICE_IDENTIFIERS.IResetDataFactory), gameName).asStream(stream$, error$, connectionStatus$);
    this.nextGame$ = new NextGameResponse(container.get(SERVICE_IDENTIFIERS.INextGameDataFactory), gameName).asStream(stream$, error$, connectionStatus$);
    this.prizeWinner$ = new PrizeWinnerResponse(container.get(SERVICE_IDENTIFIERS.IPrizeWinnerDataFactory), gameName).asStream(stream$, error$, connectionStatus$);
    this.hamsterRaceInfo$ = new HamsterRaceInfoResponse().asStream(stream$, error$, connectionStatus$);
    this.winnerCatchUp$ = new WinnerCatchupResponse().asStream(stream$, error$, connectionStatus$);
    this.setStage$ = new SetStageResponse().asStream(stream$, error$, connectionStatus$);
    this.boxNumbers$ = new BoxNumbersResponse(container.get(SERVICE_IDENTIFIERS.IBoxNumbersDataFactory), gameName).asStream(stream$, error$, connectionStatus$);
  }

  public static createStreamsForGame(gameIdentity: GameIdentity): IServerResponseStreamsResolver {
    return (stream$, error$, connectionStatus$) => new GameServerResponseStreams(stream$, error$, connectionStatus$, gameIdentity);
  }
}

export { GameServerResponseStreams, JsonGameServerResponseStreams };



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/components/game/_domain/server/_factory/stream-factory.ts